import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useAuth } from "@core/contexts/AuthContext";
import { BetaKeyService } from "@core/services/BetaKeyService";
import { useToast } from "@core/hooks/use-toast";
import { Key, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

interface BetaKeyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BetaKeyModal({ open, onOpenChange }: BetaKeyModalProps) {
  const { t } = useTranslation("polls");
  const [code, setCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user, session } = useAuth();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      toast({
        title: t("beta_key.error_title"),
        description: t("beta_key.must_be_logged_in"),
        variant: "destructive",
      });
      return;
    }

    if (!code.trim()) {
      toast({
        title: t("beta_key.error_title"),
        description: t("beta_key.enter_code"),
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await BetaKeyService.redeemKey(
        user.id,
        code,
        session?.access_token,
      );

      if (result.success) {
        toast({
          title: t("beta_key.activated_title"),
          description: t("beta_key.activated_description", {
            credits: result.credits,
          }),
        });
        setCode("");
        onOpenChange(false);
        // Recharger la page pour mettre à jour les quotas
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        toast({
          title: t("beta_key.error_title"),
          description: result.error || t("beta_key.activation_failed"),
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: t("beta_key.error_title"),
        description: t("beta_key.unexpected_error"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCode = (value: string) => {
    // Normaliser : enlever espaces, mettre en majuscules
    const normalized = value.replace(/\s/g, "").toUpperCase();
    // Formater : BETA-XXXX-XXXX-XXXX
    if (normalized.startsWith("BETA-")) {
      return normalized;
    }
    if (normalized.length > 0 && !normalized.startsWith("BETA-")) {
      return `BETA-${normalized}`;
    }
    return normalized;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            {t("beta_key.title")}
          </DialogTitle>
          <DialogDescription>
            {t("beta_key.description")}
            <br />
            <span className="text-xs text-muted-foreground mt-2 block">
              {t("beta_key.format_hint")}
            </span>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="beta-key">{t("beta_key.code_label")}</Label>
            <Input
              id="beta-key"
              type="text"
              placeholder="BETA-XXXX-XXXX-XXXX"
              value={code}
              onChange={(e) => setCode(formatCode(e.target.value))}
              maxLength={20}
              disabled={isSubmitting}
              className="font-mono uppercase"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
              data-testid="betakeymodal-annuler"
            >
              {t("beta_key.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !code.trim()}
              data-testid="betakeymodal-submit"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("beta_key.activating")}
                </>
              ) : (
                t("beta_key.activate")
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
